/**
 * PageHeader — título, descrição e as duas ações que toda página precisa.
 *
 * `atualizar` e `voltar` moram aqui, e não em cada página, porque a ausência
 * deles era sentida em TODAS: o operador olhava um número de cinco minutos
 * atrás sem ter como pedir o de agora, e recorria ao F5 — que na PWA instalada
 * nem sempre existe como gesto.
 *
 * O botão de atualizar não recarrega a página: ele invalida as queries do
 * React Query, que é o que traz dado novo sem perder o estado da tela (filtro
 * escolhido, modal aberto, posição da rolagem). Recarregar jogaria tudo fora
 * pra buscar o que já dá pra buscar sozinho.
 */

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useVoltar } from "@/lib/voltar";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import clsx from "clsx";

interface Props {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Mostra o botão de voltar. Use em tela de detalhe, não em tela de topo. */
  voltar?: boolean;
  /**
   * Pra onde ir quando NÃO há de onde voltar.
   *
   * `navigate(-1)` sozinho tem um buraco que aparece justamente nas telas
   * que abrimos em aba nova: aba recém-aberta tem histórico vazio, e voltar
   * dali não leva a lugar nenhum do app — quando leva, é pra fora dele. O
   * mesmo acontece em link colado, favorito e recarregamento.
   *
   * O React Router marca a primeira entrada do histórico com
   * `location.key === "default"`, e é assim que dá pra distinguir "cheguei
   * navegando" de "esta aba começou aqui". No primeiro caso o botão volta
   * de verdade; no segundo, vai pro pai declarado aqui.
   */
  voltarPara?: string;
  /**
   * Chaves de query a revalidar. Vazio revalida TUDO que está montado, que é
   * o comportamento certo pro botão genérico: a pessoa que clica em atualizar
   * quer a tela inteira fresca, não uma parte dela.
   */
  atualizar?: readonly unknown[][];
  /** Esconde o botão de atualizar em telas sem dado vivo (formulários). */
  semAtualizar?: boolean;
}

export function PageHeader({
  title,
  description,
  actions,
  voltar,
  voltarPara,
  atualizar,
  semAtualizar,
}: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const voltarAgora = useVoltar(voltarPara ?? "/");
  const [girando, setGirando] = useState(false);

  async function recarregar() {
    setGirando(true);
    try {
      if (atualizar?.length) {
        await Promise.all(
          atualizar.map((chave) => qc.invalidateQueries({ queryKey: chave })),
        );
      } else {
        await qc.invalidateQueries();
      }
    } finally {
      // Mínimo de meio segundo girando. Sem isso, uma resposta de cache
      // instantânea não produz animação nenhuma e o clique parece ter falhado.
      setTimeout(() => setGirando(false), 500);
    }
  }

  return (
    <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {voltar && (
          <button
            type="button"
            onClick={voltarAgora}
            aria-label="Voltar"
            title="Voltar"
            className="mt-1 rounded-lg border border-surface-200 p-2 text-slate-500 hover:bg-surface-100 hover:text-slate-700 dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
          {description && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {actions}
        {!semAtualizar && (
          <button
            type="button"
            onClick={() => void recarregar()}
            disabled={girando}
            aria-label="Atualizar"
            title="Buscar dados atualizados"
            className="rounded-lg border border-surface-200 p-2 text-slate-500 hover:bg-surface-100 hover:text-slate-700 disabled:opacity-60 dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <RefreshCw className={clsx("h-4 w-4", girando && "animate-spin")} />
          </button>
        )}
      </div>
    </div>
  );
}
